import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from 'utils/supabase'
import { VoterTypes } from 'admin/pages/Voters'
import styled from 'styled-components'
import { stateTypes } from 'admin/pages/Candidates'
import { isNilOrEmpty } from 'utils/ramda'
import { CircularProgress } from '@mui/material'
import InitialVotingTable, { InitialVotingTableRowTypes } from 'admin/components/InitialVotingTable'
import toast from 'react-hot-toast'
import Modal from 'components/Modal'
import Button from 'components/Button'
import { mobileCss } from 'utils/theme'

const initialValues = {
  success: [],
  innovation: [],
  charity: []
}

const InitialVoting = () => {
  const [voter, setVoter] = useState<VoterTypes | undefined>(undefined)
  const [candidates, setCandidates] = useState<stateTypes>(initialValues)
  const [votes, setVotes] = useState<{ [key: string]: { value: boolean, nominee: string } }>({})
  const [loading, setLoading] = useState(isNilOrEmpty(candidates))
  const { code } = useParams()
  const navigate = useNavigate()
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const isSaved = voter?.initial_voting_saved || false
  const [isVotingActive, setIsVotingActive] = useState(false)

  const handleConfirmModalOpen = () => setConfirmModalOpen(true)
  const handleConfirmModalClose = () => setConfirmModalOpen(false)

  useEffect(() => {
    if (candidates && voter) {
      const group = [...candidates.success, ...candidates.charity, ...candidates.innovation]

      const initialValues = group.reduce((acc, candidate) => {
        const { nominee, id } = candidate
        const currentVotes = voter?.votes_initial || {}
        const existingVote = currentVotes[id]

        if (existingVote) {
          // @ts-ignore
          acc[id] = {
            nominee,
            value: existingVote.value
          }
        } else {
          // @ts-ignore
          acc[id] = {
            nominee,
            value: false
          }
        }

        return acc
      }, {})

      setVotes(initialValues)
    }
  }, [candidates, voter])

  const getVoter = async () => {
    try {
      const response = await supabase
        .from('voters')
        .select()
        .eq('code', code)

      const voterData = response.data ? response.data[0] : null

      if (voterData) {
        setVoter(voterData)
      }
    } catch (err) {
      console.error(err)
    }
  }
  const getSettings = async () => {
    try {
      const response = await supabase
        .from('panel_activity')
        .select()
        .eq('slug', 'settings')

      const settingsData = response.data ? response.data[0] : null

      if (settingsData) {
        setIsVotingActive(settingsData.voting_active)
      }
    } catch (err) {
      console.error(err)
    }
  }

  const getCandidates = async () => {
    try {
      const response = await supabase
        .from('candidates')
        .select()

      const data = response.data as InitialVotingTableRowTypes[]

      const success = data.filter(candidate => candidate.type === 'success')
      const innovation = data.filter(candidate => candidate.type === 'innovation')
      const charity = data.filter(candidate => candidate.type === 'charity')

      setCandidates({
        success,
        innovation,
        charity
      })
      setLoading(false)
    } catch (err) {
      console.error(err)
    }
  }

  useEffect(() => {
    getVoter()
    getSettings()
    getCandidates()
  }, [])

  const handleSubmit = async () => {
    try {
      const values = {
        ...voter,
        votes_initial: votes
      }

      await supabase.from('voters').update([values]).eq('id', voter?.id || '')

      toast.success('Pomyślnie zapisano głosy')
    } catch (err) {
      toast.error('Wystąpił błąd podczas zapisu')
      console.error(err)
    }
  }

  const handleSaveForEver = async () => {
    try {
      const values = {
        ...voter,
        votes_initial: votes,
        initial_voting_saved: true
      }

      await supabase.from('voters').update([values]).eq('id', voter?.id || '')
      getVoter()
      handleConfirmModalClose()
      toast.success('Pomyślnie zatwierdzono głosy')
    } catch (err) {
      toast.error('Wystąpił błąd')
      handleConfirmModalClose()
      console.error(err)
    }
  }

  const handleUpdateVote = (candidateId: string, value: boolean) => {
    setVotes(prev => ({
      ...prev,
      [candidateId]: {
        ...prev[candidateId],
        value
      }
    }))
  }

  return loading
    ? (
      <EmptyState>
        <CircularProgress size={60} />
      </EmptyState>
    )
    : isVotingActive
      ? (
      <ContentWrapper>
        Witaj, {voter?.name} - głosujesz z linku /preselekcja/{voter?.code}
        <Header>
          <InfoWrapper>
            <Title>
              Booklet Nagrody PRB
            </Title>
            <HeadLine>Zapraszamy do zapoznania się z listą kandydatur{' '}
              <InlineLink target='_blank' href='/broszura/wszyscy'>
                TUTAJ{' '}
              </InlineLink>. Prosimy o zaznaczenie przy każdej z nich przycisku TAK lub NIE, aby wskazać, czy kandydatura powinna być rozpatrywana przez Kapitułę na dalszych etapach konkursu.
            </HeadLine>
          </InfoWrapper>
          {/*<Button variant='outlined' onClick={() => navigate('/broszura/wszyscy')}>Zobacz wszystkie</Button>*/}
        </Header>
        <TableWrapper>
          <TableTitle>
            Sukces
            <ButtonsWrapper>
              <VotersButton onClick={() => navigate('/broszura/sukces')}>Zobacz całą kategorię</VotersButton>
            </ButtonsWrapper>
          </TableTitle>
          <InitialVotingTable
            onVote={handleUpdateVote}
            votes={votes}
            candidates={candidates.success}
            voter={voter}
          />
          {
            !isSaved && (
              <ButtonsWrapper>
                <VotersButton onClick={handleSubmit}>Zapisz</VotersButton>
              </ButtonsWrapper>
            )
          }
        </TableWrapper>
        <TableWrapper>
          <TableTitle>
            Wizja i innowacje
            <ButtonsWrapper>
              <VotersButton onClick={() => navigate('/broszura/innowacje')}>Zobacz całą kategorię</VotersButton>
            </ButtonsWrapper>
          </TableTitle>
          <InitialVotingTable
            onVote={handleUpdateVote}
            votes={votes}
            candidates={candidates.innovation}
            voter={voter}
          />
          {
            !isSaved && (
              <ButtonsWrapper>
                <VotersButton onClick={handleSubmit}>Zapisz</VotersButton>
              </ButtonsWrapper>
            )
          }
        </TableWrapper>
        <TableWrapper>
          <TableTitle>
            Działalność społeczna
            <ButtonsWrapper>
              <VotersButton onClick={() => navigate('/broszura/dzialalnosc-spoleczna')}>Zobacz całą kategorię</VotersButton>
            </ButtonsWrapper>
          </TableTitle>
          <InitialVotingTable
            onVote={handleUpdateVote}
            votes={votes}
            candidates={candidates.charity}
            voter={voter}
          />
          {
            !isSaved && (
              <ButtonsWrapper>
                <VotersButton onClick={handleSubmit}>Zapisz</VotersButton>
              </ButtonsWrapper>
            )
          }
        </TableWrapper>
        {
          !isSaved && (
            <>
              <VotersButton onClick={handleConfirmModalOpen}>Zatwierdź formularz</VotersButton>
              <Modal
                open={confirmModalOpen}
                onClose={handleConfirmModalClose}
                title='Potwierdź wysłanie formularza'
              >
                Czy na pewno chcesz zatwierdzić swoje głosy? Pamiętaj, że po zatwierdzeniu nie będzie możliwości ich zmiany.
                <ModalButtons>
                  <VotersButton onClick={handleSaveForEver}>Potwierdź</VotersButton>
                  <VotersButton onClick={handleConfirmModalClose}>Anuluj</VotersButton>
                </ModalButtons>
              </Modal>
            </>
          )
        }
      </ContentWrapper>
    )
      : (
        <div>głosowanie nieaktywne</div>
      )
}

export default InitialVoting

const ContentWrapper = styled.div`
  width: 80%;
  display: flex;
  flex-direction: column;
  padding: 16px;

  ${mobileCss(`
    width: 90%;
    padding: 16px 0;
  `)};
`
const EmptyState = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  gap: 20px;
`

const VotersButton = styled(Button)`
  border: 1px solid ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primary};
  margin: 0;
`

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 10px;
`

const Title = styled.div`
color: ${({ theme }) => theme.colors.primary};
  font-size: 32px;
`

const HeadLine = styled.div`
  font-size: 20px;
`
const TableWrapper = styled.div`
  margin: 40px 0;
`
const TableTitle = styled.div`
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 16px;
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
  
  ${mobileCss(`
    padding: 0;
  `)};
`

const ModalButtons = styled.div`
  margin-top: 30px;
  gap: 16px;
  display: flex;
  justify-content: center;
  width: 100%;
`

const InlineLink = styled.a`
  text-decoration: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 18px;
  font-weight: 700;
`
